import { useEffect, useMemo } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { DatePicker, Form, Select, Switch } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useAppDispatch, useAppSelector } from 'core/store/hooks';
import AuthService from 'core/auth/auth.service';
import LinksButton from 'shared/design-system/components/links-button';
import TagMultipleSelect from 'shared/design-system/components/app-tag-multiple-select';
import { filterOption } from 'shared/utils/common.utils';
import { getClFilters } from 'pages/private/management-tools/_redux/actions';
import { filtriClSelector } from 'pages/private/management-tools/_redux/selectors';
import { AgendaCommercialeSearchDto } from '@api/client';
import { getAgendaFilterOptions } from '../../_redux/actions';
import { agendaFilterOptionsSelector } from '../../_redux/selectors';

interface Props {
  onSearch: (filters: Partial<AgendaCommercialeSearchDto>) => void;
  onReset: () => void;
  hideDateFilters?: boolean;
  calendarSpacer?: boolean;
}

const AgendaActionsFilters: React.FC<Props> = ({ onSearch, onReset, hideDateFilters = false, calendarSpacer = false }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const agendaFilterOptions = useAppSelector(agendaFilterOptionsSelector);
  const filtriCl = useAppSelector(filtriClSelector);
  const isAdmin = AuthService.isSuperAdmin();

  const businessUnits = useMemo(
    () => (isAdmin ? filtriCl?.businessUnit : filtriCl?.onlyChildBusinessUnit) || [],
    [isAdmin, filtriCl?.businessUnit, filtriCl?.onlyChildBusinessUnit],
  );

  useEffect(() => {
    if (!agendaFilterOptions?.accounts?.length) dispatch(getAgendaFilterOptions());
    if (!filtriCl?.businessUnit?.length && !filtriCl?.onlyChildBusinessUnit?.length) {
      dispatch(getClFilters());
    }
  }, []);

  const handleFinish = (values: any) => {
    const filters: Partial<AgendaCommercialeSearchDto> = {};
    if (values.fkCliente) filters.fkCliente = values.fkCliente;
    if (values.fkStrutturaBusiness?.length) filters.fkStrutturaBusiness = values.fkStrutturaBusiness;
    if (values.statiAttivita?.length) filters.statiAttivita = values.statiAttivita;
    if (values.isRicercaEstesa) filters.isRicercaEstesa = values.isRicercaEstesa;
    if (!hideDateFilters) {
      if (values.actionDateDa) filters.actionDateDa = new Date(values.actionDateDa.format('YYYY-MM-DD'));
      if (values.actionDateA) filters.actionDateA = new Date(values.actionDateA.format('YYYY-MM-DD'));
    }
    onSearch(filters);
  };

  const handleReset = () => {
    form.resetFields();
    onReset();
  };

  // In modalità lista vogliamo 5 filtri sulla stessa riga (20% ciascuno su lg+)
  // tramite Bootstrap row-cols-lg-5; per il fallback responsive teniamo xs/sm/md.
  const colProps = hideDateFilters
    ? { xs: 12, sm: 12, md: 12, lg: 12 }
    : { xs: 12, sm: 12, md: 6 };
  const dateColProps = { xs: 12, sm: 12, md: 6 };

  return (
    <Form form={form} onFinish={handleFinish} layout="vertical">
      {calendarSpacer && <div className="agenda-calendar-filter-spacer" />}
      <Row className={hideDateFilters ? '' : 'row-cols-lg-5'}>
        <Col {...colProps}>
          <Form.Item name="fkCliente" label={t('Cliente')} className="mb-2">
            <Select
              showSearch
              allowClear
              placeholder={t('Seleziona cliente')}
              optionFilterProp="children"
              filterOption={filterOption}
              getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
            >
              {agendaFilterOptions?.accounts?.map((el) => (
                <Select.Option key={el.idAccount} value={el.idAccount}>
                  {el.nome}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col {...colProps}>
          <Form.Item name="statiAttivita" label={t('Stato attività')} className="mb-2">
            <Select
              mode="multiple"
              allowClear
              tagRender={TagMultipleSelect}
              placeholder={t('Seleziona stato')}
              getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
            >
              {(filtriCl?.statiAttivita || []).map((s) => (
                <Select.Option key={s} value={s}>
                  {s.replace(/_/g, ' ')}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col {...colProps}>
          <Form.Item name="fkStrutturaBusiness" label={t('Struttura di business')} className="mb-2">
            <Select
              mode="multiple"
              showSearch
              allowClear
              placeholder={t('Seleziona struttura')}
              optionFilterProp="children"
              filterOption={filterOption}
              getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
            >
              {businessUnits.map((el) => (
                <Select.Option key={el.id} value={el.id}>
                  {el.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        {!hideDateFilters && (
          <>
            <Col {...dateColProps}>
              <Form.Item name="actionDateDa" label={t('Data azione da')} className="mb-2">
                <DatePicker
                  className="w-100"
                  format="DD/MM/YYYY"
                  allowClear
                  placeholder={t('Seleziona data')}
                  suffixIcon={<i className="far fa-calendar text-blue-links" />}
                />
              </Form.Item>
            </Col>

            <Col {...dateColProps}>
              <Form.Item
                name="actionDateA"
                label={t('Data azione a')}
                className="mb-2"
                dependencies={['actionDateDa']}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const da = getFieldValue('actionDateDa');
                      if (!value || !da || !dayjs(value).isBefore(dayjs(da), 'day')) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error(t('La data "a" deve essere successiva alla data "da"'))
                      );
                    },
                  }),
                ]}
              >
                <DatePicker
                  className="w-100"
                  format="DD/MM/YYYY"
                  allowClear
                  placeholder={t('Seleziona data')}
                  suffixIcon={<i className="far fa-calendar text-blue-links" />}
                />
              </Form.Item>
            </Col>
          </>
        )}

        <Col {...colProps}>
          <Form.Item
            name="isRicercaEstesa"
            label={t('Includi clienti associati nel budget')}
            className="mb-2"
            valuePropName="checked"
            initialValue={false}
          >
            <Switch />
          </Form.Item>
        </Col>
      </Row>

      <div className={`d-flex ${hideDateFilters ? 'flex-column gap-2' : 'mb-3'}`}>
        <div className={`d-flex ${hideDateFilters ? 'flex-column gap-2' : 'ms-auto'}`}>
          <Button className="btn text-danger trash-btn" type="button" variant="icon" onClick={handleReset}>
            <i className="fa-regular fa-trash-can me-2" />
            {t('project-list.delete-filters')}
          </Button>
          <LinksButton type="submit" className={`btn-primary btn-links ${hideDateFilters ? 'w-100' : 'btn-sm px-4'}`}>
            <i className="fa-solid fa-magnifying-glass me-2" />
            {t('project-list.search')}
          </LinksButton>
        </div>
      </div>
    </Form>
  );
};

export default AgendaActionsFilters;
